import {
  FOODS,
  GROUP_LABELS,
  GROUP_ONE,
  foodsInGroup,
  type Food,
  type FoodGroup,
} from "./market-config";

/**
 * Makola Market Plate rules: what a round asks for, what the belt carries,
 * and how the curve tightens.
 *
 * Same split as the other two games — pure logic here, React and WebGL
 * elsewhere — so the curve can be reasoned about and tested. The numbers
 * mirror the server's MAKOLA constants, which bound a submitted score.
 */

export const LIVES = 3;
/**
 * The bar for "that went well": two rounds cleared at the opening quota
 * scores about 146. Round it to 150 — clearing it, or beating your own best,
 * earns praise rather than a taunt.
 */
export const GOOD_SCORE = 150;

export const BEST_SCORE_KEY = "@MakolaBestScore";
/** Scores stop growing here, so the ceiling stays finite. The DIFFICULTY does
 *  not stop — see OVERTIME_FROM_ROUND. */
export const MAX_ROUND = 30;

/** Where the second, slower squeeze begins: after this the belt keeps
 *  quickening until the stall is unshoppable, which is how an uncapped run
 *  ends. A run has no cap, so it has to finish by becoming impossible rather
 *  than by becoming boring (Ken, 2026-09-04). */
export const OVERTIME_FROM_ROUND = 9;
/** Backstops, not difficulty settings. At a 1.3s crossing with items 0.3s
 *  apart the stall cannot be shopped, and the run is over. */
export const CROSS_HARD_FLOOR_MS = 1_300;
export const SPAWN_HARD_FLOOR_MS = 300;
export const DURATION_HARD_FLOOR_MS = 9_000;

/** Two kinds of ask. A group round is selective attention; a plate round
 *  adds working memory, because you have to hold what you already have. */
export type GoalKind = "group" | "plate";

export interface Goal {
  kind: GoalKind;
  /** For a group round: the group being asked for. */
  group?: FoodGroup;
  /** For a plate round: one of each of these. */
  plate?: FoodGroup[];
  /** How many correct grabs the round needs. */
  quota: number;
  label: string;
}

export interface RoundPlan {
  round: number;
  goal: Goal;
  /** How long the belt runs. */
  durationMs: number;
  /** Seconds one item takes to cross the belt — smaller is faster. */
  crossMs: number;
  /** Gap between items appearing. */
  spawnEveryMs: number;
}

const PLATE = ["starch", "protein", "vegetable"] as const;

/**
 * The curve tightens the belt and lengthens the ask, and swaps in plate
 * rounds from round three. Plate rounds arrive later because they change what
 * the game IS — from spotting one thing to tracking three — and that is a
 * bigger jump than any amount of speed.
 */
export function planRound(round: number, random: () => number = Math.random): RoundPlan {
  const capped = Math.min(round, MAX_ROUND);
  const isPlate = capped >= 3 && capped % 3 === 0;

  const goal: Goal = isPlate
    ? {
        kind: "plate",
        plate: [...PLATE],
        quota: PLATE.length,
        label: `Build a plate — ${PLATE.map((group) => GROUP_ONE[group]).join(", ")}`,
      }
    : (() => {
        const groups: FoodGroup[] = ["vegetable", "protein", "starch", "fruit"];
        const group = groups[Math.floor(random() * groups.length)];
        const quota = Math.min(6, 3 + Math.floor((capped - 1) / 2));
        return {
          kind: "group" as const,
          group,
          quota,
          label: `Take ${quota} — ${GROUP_LABELS[group].toLowerCase()}`,
        };
      })();

  // Past the first plateau everything keeps tightening, slower but without a
  // floor to hide behind.
  const overtime = Math.max(0, round - OVERTIME_FROM_ROUND);

  return {
    round,
    goal,
    durationMs: Math.max(
      DURATION_HARD_FLOOR_MS,
      Math.max(13_000, 20_000 - (capped - 1) * 550) - overtime * 200,
    ),
    /**
     * The belt speeds up, with a floor that keeps an item on screen long
     * enough to read its name.
     *
     * The step is 420ms, not the 240ms it started as (Ken, 2026-09-04 —
     * "does it even progress?"). At 240ms each round was 4% faster than the
     * last, which is under what anyone notices: the curve was real but
     * invisible until round eight. At 420ms a round is ~7% faster than the
     * one before and the belt is a third quicker by round five, which is felt
     * rather than measured.
     */
    crossMs: Math.max(
      CROSS_HARD_FLOOR_MS,
      Math.max(2_800, 6_000 - (capped - 1) * 420) - overtime * 150,
    ),
    spawnEveryMs: Math.max(
      SPAWN_HARD_FLOOR_MS,
      Math.max(600, 1_200 - (capped - 1) * 75) - overtime * 30,
    ),
  };
}

/** True when this food satisfies the goal, given what is already on the
 *  plate. */
export function isWanted(goal: Goal, food: Food, taken: FoodGroup[]): boolean {
  if (goal.kind === "group") return food.group === goal.group;
  if (!goal.plate?.includes(food.group)) return false;
  // A plate wants one of each: a second starch is not wrong food, it is the
  // wrong TIME for that food, and it still counts as a mistake.
  return !taken.includes(food.group);
}

/** What one correct grab is worth. Later rounds pay more, and a faster belt
 *  pays more, so surviving the curve beats replaying round one. */
export function grabScore(round: number, crossMs: number): number {
  const capped = Math.min(round, MAX_ROUND);
  const speedBonus = Math.round((6_000 - crossMs) / 400);
  return 10 + capped * 2 + Math.max(0, speedBonus);
}

/** Paid for filling the round's quota. */
export function roundBonus(round: number, kind: GoalKind): number {
  const capped = Math.min(round, MAX_ROUND);
  // A plate is the harder ask, so it pays more.
  return (kind === "plate" ? 40 : 25) + capped * 5;
}

/**
 * The belt for one round: a mix that always carries enough of what is wanted
 * to fill the quota, plus enough of everything else to make not-grabbing a
 * decision.
 */
export function buildBelt(
  plan: RoundPlan,
  random: () => number = Math.random,
): Food[] {
  const count = Math.ceil(plan.durationMs / plan.spawnEveryMs);
  const wanted =
    plan.goal.kind === "group"
      ? foodsInGroup(plan.goal.group as FoodGroup)
      : (plan.goal.plate ?? []).flatMap((group) => foodsInGroup(group));

  const belt: Food[] = [];
  // Roughly half the belt is takeable. Below that the round turns into
  // waiting; above it, nothing is being resisted.
  for (let i = 0; i < count; i += 1) {
    const takeable = random() < 0.5;
    const pool = takeable
      ? wanted
      : FOODS.filter((food) => !wanted.some((want) => want.key === food.key));
    const source = pool.length > 0 ? pool : FOODS;
    belt.push(source[Math.floor(random() * source.length)]);
  }

  // Guarantee the quota is actually reachable — a round nobody can complete
  // is a bug, not a difficulty.
  const takeableCount = belt.filter((food) =>
    wanted.some((want) => want.key === food.key),
  ).length;
  for (let i = takeableCount; i < plan.goal.quota; i += 1) {
    belt[i * 2] = wanted[Math.floor(random() * wanted.length)];
  }

  return belt;
}

export function readBestScore(): number {
  try {
    const raw = localStorage.getItem(BEST_SCORE_KEY);
    const value = raw ? Number(raw) : 0;
    return Number.isFinite(value) && value > 0 ? value : 0;
  } catch {
    return 0;
  }
}

export function writeBestScore(score: number): void {
  try {
    if (score > readBestScore()) {
      localStorage.setItem(BEST_SCORE_KEY, String(score));
    }
  } catch {
    // storage unavailable — the run still works, it just is not remembered
  }
}

/** End-of-run lines. Same rule as the other games: the joke is aimed at the
 *  play, never at anyone's health or family. */
export const TAUNTS = [
  "Chale, market no wait for you!",
  "Massa, you dey buy or you dey look? 😂",
  "You no serious!",
  "Ei! Ei! Ei! 🤣",
  "Chairman, w'akyi gu hɔ!",
  "W'asaaaa!",
];

export function pickTaunt(
  exclude?: string,
  random: () => number = Math.random,
): string {
  const pool =
    TAUNTS.length > 1 && exclude
      ? TAUNTS.filter((taunt) => taunt !== exclude)
      : TAUNTS;
  return pool[Math.floor(random() * pool.length)];
}
